import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, Buscador, Chip, Filtro, IconoMas, Listado, useBusquedaDiferida, type Columna, type ElementoMenu } from '@tallerya/ui';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { EliminarProveedor } from './EliminarProveedor';
import { useCambiosProveedor, useProveedores, version, type FiltrosProveedores, type Proveedor } from './datos';

// El listado de proveedores (#56): «Proveedores - Listado» del Figma y sus
// variantes (Menú de fila), (Sin resultados), (Sin proveedores) y (Eliminar).
// Sigue a `ListadoSucursales.tsx`, que es la pantalla de muestra y explica el orden.
//
// La fila abre la ficha (`FichaProveedor`, #188). Alta y edición van en su
// propia página (`FormularioProveedor`). Eliminar, en una ventana sobre el listado.
//
// **Los filtros viven en la URL** (`?buscar=&estado=activos`), como en Clientes:
// desde que la fila abre una ficha, «Atrás» tiene que traerlos de vuelta, y en
// el estado de la pantalla se perdían al salir de ella. La página no: el cursor
// es opaco, y al volver se empieza por la primera.

/** Los filtros de la URL. Los parámetros se llaman como los campos. */
type FiltrosUrl = Omit<FiltrosProveedores, 'cursor'>;

/** Un valor que no se reconoce —un enlace viejo o editado a mano— cuenta como «Todos». */
function leerFiltros(params: URLSearchParams): FiltrosUrl {
  const estado = params.get('estado');
  return {
    buscar: params.get('buscar') ?? '',
    estado: estado === 'activos' || estado === 'inactivos' ? estado : 'todos',
  };
}

export function ListadoProveedores() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const cambios = useCambiosProveedor();
  useTituloPantalla(t('admin.proveedores.titulo'));

  const [params, setParams] = useSearchParams();
  const url = leerFiltros(params);
  const busqueda = params.toString();

  const [texto, setTexto] = useState(url.buscar);
  const [cursor, setCursor] = useState<string | null>(null);
  // Por dónde se pasó: la API solo sabe ir hacia adelante.
  const [anteriores, setAnteriores] = useState<(string | null)[]>([]);
  const [eliminando, setEliminando] = useState<Proveedor | null>(null);
  const { hacer, problema } = useAccionDeFila();

  // Cuando la URL cambia —un filtro, «Atrás», o tocar «Proveedores» en el menú,
  // que la deja limpia— se vuelve a la primera página, y la caja muestra el
  // texto de la URL. Se ajusta al dibujar, como en `ListadoClientes`, y no en un
  // efecto que dibujaría primero lo viejo.
  const [vista, setVista] = useState({ busqueda, buscar: url.buscar });
  if (busqueda !== vista.busqueda) {
    setVista({ busqueda, buscar: url.buscar });
    setCursor(null);
    setAnteriores([]);
    if (url.buscar !== vista.buscar) setTexto(url.buscar);
  }

  /** Reemplaza la entrada del historial: «Atrás» sale del listado, no deshace un filtro por tecla. */
  function filtrar(cambio: Partial<FiltrosUrl>) {
    setParams(
      (actuales) => {
        const nuevos = new URLSearchParams(actuales);
        for (const [clave, valor] of Object.entries(cambio)) {
          // «Todos» es el valor de los combos; en el buscador es un texto como cualquiera.
          if (valor && (clave === 'buscar' || valor !== 'todos')) nuevos.set(clave, valor);
          else nuevos.delete(clave);
        }
        return nuevos;
      },
      { replace: true },
    );
  }

  useBusquedaDiferida(texto, (buscar) => filtrar({ buscar }), { pendiente: texto !== url.buscar });

  const filtros: FiltrosProveedores = { ...url, cursor };
  const consulta = useProveedores(filtros);
  const pagina = consulta.data;

  // `desdeListado`: la ficha y el formulario vuelven con «Atrás», y el listado
  // regresa con sus filtros. Si el proveedor se elimina desde su edición, se salta
  // hasta acá en vez de a una ficha que ya no existe.
  const verFicha = (p: Proveedor) => void navegar(`/proveedores/${String(p.id)}`, { state: { desdeListado: true } });
  const editar = (p: Proveedor) =>
    void navegar(`/proveedores/${String(p.id)}/editar`, { state: { desdeListado: true } });

  function opciones(p: Proveedor): ElementoMenu[] {
    const id = String(p.id);
    const nombre = { nombre: p.name };

    return [
      { etiqueta: t('admin.proveedores.menu.verFicha'), onSeleccionar: () => verFicha(p) },
      { etiqueta: t('admin.proveedores.menu.editar'), onSeleccionar: () => editar(p) },
      p.isActive
        ? {
            etiqueta: t('admin.proveedores.menu.desactivar'),
            onSeleccionar: () =>
              void hacer(
                cambios.cambiarEstado(id, version(p.version), false),
                t('admin.proveedores.aviso.desactivado', nombre),
              ),
          }
        : {
            etiqueta: t('admin.proveedores.menu.activar'),
            onSeleccionar: () =>
              void hacer(
                cambios.cambiarEstado(id, version(p.version), true),
                t('admin.proveedores.aviso.activado', nombre),
              ),
          },
      { tipo: 'divisor' as const },
      { etiqueta: t('admin.proveedores.menu.eliminar'), peligro: true, onSeleccionar: () => setEliminando(p) },
    ];
  }

  const columnas: Columna<Proveedor>[] = [
    {
      id: 'codigo',
      titulo: t('admin.proveedores.columna.codigo'),
      ancho: 110,
      // Un botón, para llegar con el teclado: la fila entera solo responde al dedo y al mouse.
      celda: (p) => (
        <button type="button" className="tipos__enlace" onClick={() => verFicha(p)}>
          {p.code}
        </button>
      ),
      tarjeta: 'pie',
    },
    { id: 'nombre', titulo: t('admin.proveedores.columna.nombre'), celda: (p) => p.name, tarjeta: 'titulo' },
    {
      id: 'documento',
      titulo: t('admin.proveedores.columna.documento'),
      ancho: 260,
      // Tipo y número en una celda, «NIT 0614-250190-102-3»: vienen en pareja o no vienen.
      celda: (p) => (p.docIdentifier ? [p.docTypeCode, p.docIdentifier].filter(Boolean).join(' ') : '—'),
      tarjeta: 'pie',
    },
    { id: 'referencia', titulo: t('admin.proveedores.columna.referencia'), ancho: 200, celda: (p) => p.reference ?? '—' },
    {
      id: 'estado',
      titulo: t('admin.proveedores.columna.estado'),
      ancho: 150,
      celda: (p) =>
        p.isActive ? (
          <Chip tono="exito">{t('admin.proveedores.activo')}</Chip>
        ) : (
          <Chip>{t('admin.proveedores.inactivo')}</Chip>
        ),
      tarjeta: 'esquina',
    },
  ];

  const botonNuevo = (
    <Boton icono={<IconoMas />} onClick={() => void navegar('/proveedores/nuevo')}>
      {t('admin.proveedores.nuevo')}
    </Boton>
  );

  return (
    <>
      {problema && <AvisoError aviso={{ titulo: t('admin.proveedores.error.titulo'), texto: problema }} />}

      <Listado
        titulo={t('admin.proveedores.titulo')}
        info={t('admin.proveedores.info')}
        columnas={columnas}
        filas={pagina?.items}
        claveFila={(p) => String(p.id)}
        estado={consulta.isPending ? 'cargando' : consulta.isError ? 'error' : 'listo'}
        onReintentar={() => void consulta.refetch()}
        filtros={
          <>
            <Buscador valor={texto} onCambiar={setTexto} placeholder={t('admin.proveedores.buscar')} />
            <Filtro
              etiqueta={t('admin.proveedores.filtro.estado')}
              valor={filtros.estado}
              onCambiar={(valor) => filtrar({ estado: valor as FiltrosProveedores['estado'] })}
              opciones={[
                { valor: 'todos', etiqueta: t('admin.proveedores.filtro.todos') },
                { valor: 'activos', etiqueta: t('admin.proveedores.filtro.activos') },
                { valor: 'inactivos', etiqueta: t('admin.proveedores.filtro.inactivos') },
              ]}
            />
          </>
        }
        acciones={botonNuevo}
        // Una página que no es la primera cuenta como filtro: si queda vacía, el
        // taller no está vacío. Ver `ListadoSucursales.tsx`.
        hayFiltros={filtros.buscar !== '' || filtros.estado !== 'todos' || filtros.cursor !== null}
        onLimpiarFiltros={() => {
          setTexto('');
          // La primera página aunque la URL ya estuviera limpia: limpiar desde
          // una página 2 vacía también tiene que volver al inicio.
          setCursor(null);
          setAnteriores([]);
          setParams({}, { replace: true });
        }}
        vacio={{
          titulo: t('admin.proveedores.vacio.titulo'),
          texto: t('admin.proveedores.vacio.texto'),
          acciones: botonNuevo,
        }}
        {...(filtros.buscar && {
          detalleSinResultados: t('admin.proveedores.sinResultados', { texto: filtros.buscar }),
        })}
        opcionesFila={opciones}
        onFila={verFicha}
        paginacion={{
          hayAnterior: anteriores.length > 0,
          hayMas: pagina?.hasMore ?? false,
          onAnterior: () => {
            setCursor(anteriores.at(-1) ?? null);
            setAnteriores((a) => a.slice(0, -1));
          },
          onSiguiente: () => {
            if (!pagina?.nextCursor) return;
            setAnteriores((a) => [...a, cursor]);
            setCursor(pagina.nextCursor);
          },
        }}
      />

      <EliminarProveedor proveedor={eliminando} onCerrar={() => setEliminando(null)} />
    </>
  );
}
